import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react'
import useSWR from 'swr'
import { apiGet } from '../../config/api'
import {
  packageCatalog as staticPackageCatalog,
  type PackageCatalogItem,
} from './packageCatalog'
import { normalizePackageSlug } from '@proffer/shared'

const CORE_PACKAGE_SLUGS = ['basic', 'pro', 'business'] as const

type MarketingCopyKey =
  | 'name'
  | 'stage'
  | 'description'
  | 'plainSummary'
  | 'features'
  | 'detailTitle'
  | 'detailIntro'
  | 'supports'
  | 'examples'
  | 'marketingBadge'
  | 'featured'
  | 'ctaLabel'

const MARKETING_COPY_KEYS: MarketingCopyKey[] = [
  'name',
  'stage',
  'description',
  'plainSummary',
  'features',
  'detailTitle',
  'detailIntro',
  'supports',
  'examples',
  'marketingBadge',
  'featured',
  'ctaLabel',
]

function mergeCoreMarketingCopy(
  item: PackageCatalogItem,
  fallback: PackageCatalogItem | undefined,
): PackageCatalogItem {
  if (!fallback || !CORE_PACKAGE_SLUGS.includes(item.slug as (typeof CORE_PACKAGE_SLUGS)[number])) {
    return item
  }
  const merged = { ...item }
  for (const key of MARKETING_COPY_KEYS) {
    const value = fallback[key]
    if (value !== undefined && value !== null) {
      ;(merged as Record<MarketingCopyKey, PackageCatalogItem[MarketingCopyKey]>)[key] = value
    }
  }
  return merged
}

type PackageCatalogContextValue = {
  catalog: PackageCatalogItem[]
  isLoading: boolean
  isFromApi: boolean
}

const PackageCatalogContext = createContext<PackageCatalogContextValue>({
  catalog: staticPackageCatalog,
  isLoading: false,
  isFromApi: false,
})

export function PackageCatalogProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useSWR('public-package-catalog', () =>
    apiGet<{ packages: PackageCatalogItem[] }>('/public/packages'),
  )

  const value = useMemo<PackageCatalogContextValue>(() => {
    const fromApi = data?.packages?.length ? data.packages : null
    const catalog = (fromApi ?? staticPackageCatalog).map((item) => {
      const fallback = staticPackageCatalog.find(
        (s) => s.slug === normalizePackageSlug(item.slug),
      )
      const merged = mergeCoreMarketingCopy(
        {
          ...item,
          slug: normalizePackageSlug(item.slug),
          billingStep: item.billingStep ?? fallback?.billingStep ?? 1,
          billingSelectionMode:
            item.billingSelectionMode ?? fallback?.billingSelectionMode ?? 'exclusive',
          billingPriceKey: item.billingPriceKey ?? fallback?.billingPriceKey ?? null,
          priceMonthlyCents:
            item.priceMonthlyCents ?? fallback?.priceMonthlyCents ?? null,
          priceAnnualCents: item.priceAnnualCents ?? fallback?.priceAnnualCents ?? null,
          priceMonthlyInitialCents:
            item.priceMonthlyInitialCents ?? fallback?.priceMonthlyInitialCents ?? null,
          priceAnnualInitialCents:
            item.priceAnnualInitialCents ?? fallback?.priceAnnualInitialCents ?? null,
          includesSlugs: item.includesSlugs ?? fallback?.includesSlugs ?? [],
          featureKeys: item.featureKeys ?? fallback?.featureKeys ?? [],
        },
        fallback,
      )
      return merged
    }).filter((item) => CORE_PACKAGE_SLUGS.includes(item.slug as (typeof CORE_PACKAGE_SLUGS)[number]))
      .sort(
        (a, b) =>
          CORE_PACKAGE_SLUGS.indexOf(a.slug as (typeof CORE_PACKAGE_SLUGS)[number]) -
          CORE_PACKAGE_SLUGS.indexOf(b.slug as (typeof CORE_PACKAGE_SLUGS)[number]),
      )
    return {
      catalog,
      isLoading,
      isFromApi: Boolean(fromApi?.length),
    }
  }, [data?.packages, isLoading])

  return (
    <PackageCatalogContext.Provider value={value}>
      {children}
    </PackageCatalogContext.Provider>
  )
}

export function usePackageCatalog() {
  return useContext(PackageCatalogContext)
}

export function usePackageBySlug(slug: string | undefined) {
  const { catalog } = usePackageCatalog()
  return useMemo(() => {
    if (!slug) return null
    return catalog.find((pkg) => pkg.slug === normalizePackageSlug(slug)) ?? null
  }, [catalog, slug])
}

export function useBillingTierCatalog() {
  const { catalog } = usePackageCatalog()
  return useMemo(
    () => catalog.filter((pkg) => pkg.slug !== 'basic'),
    [catalog],
  )
}
